import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, UserCheck, Heart, Award } from 'lucide-react';

export const WhyUsBento: React.FC = () => {
    return (
        <section className="py-32 bg-white">
            <div className="container mx-auto px-6">
                <div className="text-center mb-20">
                    <span className="text-[#11BC97] font-bold tracking-widest uppercase text-sm mb-4 block">
                        Why Us?
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold mb-4 text-black">Get Taller. Go Lengthening.</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-auto md:grid-rows-2 gap-6 h-auto md:h-[900px]">
                    {/* Main Feature - Large */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className="col-span-1 md:col-span-2 md:row-span-2 bg-[#F8FDFB] p-10 md:p-14 rounded-[2.5rem] flex flex-col justify-between overflow-hidden relative group border border-[#11BC97]/10"
                    >
                        <div>
                            <div className="w-14 h-14 bg-[#11BC97] rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-[#11BC97]/30">
                                <ShieldCheck className="text-white w-8 h-8" />
                            </div>
                            <h3 className="text-3xl font-bold mb-6 text-black">Not Just Another 'Clinic'</h3>
                            <p className="text-gray-600 leading-relaxed text-lg">
                                We’re an international team of healthcare professionals who are taking a stand against poor surgical care and post-operative complications.
                            </p>
                            <p className="text-gray-600 leading-relaxed text-lg mt-4">
                                We’ve brought together some of the world’s very best orthopaedic and rehabilitation specialists to deliver trustworthy advice, expert surgical care and world-class rehabilitation at an affordable price.
                            </p>
                        </div>
                        <div className="mt-12 relative h-64 w-full rounded-3xl overflow-hidden shadow-lg">
                                      <div className="h-96 w-full rounded-3xl overflow-hidden shadow-lg">
                                            <img 
                                                src="https://images.unsplash.com/photo-1551076805-e1869033e561?q=80&w=2000&auto=format&fit=crop" 
                                                alt="Team meeting" 
                                                className="object-cover w-full h-full grayscale group-hover:grayscale-0 transition-all duration-700" 
                                            />
                                      </div>
                        </div>
                    </motion.div>

                    {/* Secondary Feature - Top Right */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="col-span-1 md:col-span-2 bg-[#11BC97] p-10 md:p-14 rounded-[2.5rem] flex flex-col justify-center text-white relative overflow-hidden shadow-xl shadow-[#11BC97]/20"
                    >
                        <Award className="w-12 h-12 mb-6 text-white" />
                        <h3 className="text-3xl font-bold mb-4">Uncompromising Care</h3>
                        <p className="text-white/90 text-lg leading-relaxed max-w-md">
                            That’s why our team includes only respected experts in their field, committed to delivering the best possible outcomes and trained in the latest techniques at some of the world’s most prestigious medical centres, including the Paley Institute.
                        </p>
                        <div className="absolute -right-20 -bottom-20 opacity-10">
                            <Award size={300} />
                        </div>
                    </motion.div>

                    {/* Third Feature - Bottom Middle */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="col-span-1 md:col-span-1 bg-black text-white p-10 rounded-[2.5rem] flex flex-col justify-between"
                    >
                        <div>
                            <Heart className="w-10 h-10 mb-6 text-[#11BC97]" />
                            <h3 className="text-2xl font-bold mb-4">Honest Advice</h3>
                            <p className="text-gray-400 leading-relaxed">
                                It’s easy to be wowed by clever marketing. But if it sounds too good to be true it probably is. We prioritize transparency.
                            </p>
                        </div>
                    </motion.div>

                    {/* Fourth Feature - Bottom Right */}
                    <motion.div 
                         initial={{ opacity: 0, scale: 0.95 }}
                         whileInView={{ opacity: 1, scale: 1 }}
                         transition={{ delay: 0.3 }}
                        className="col-span-1 md:col-span-1 bg-white p-10 rounded-[2.5rem] border border-gray-100 flex flex-col justify-between shadow-xl shadow-gray-100/50 hover:shadow-2xl transition-all"
                    >
                        <div>
                            <UserCheck className="w-10 h-10 mb-6 text-[#11BC97]" />
                            <h3 className="text-2xl font-bold mb-4 text-black">Complication Support</h3>
                            <p className="text-gray-600 leading-relaxed">
                                If you’ve already developed a complication, or lost confidence in your current provider, we’re here to help.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};